'use client';

import React from 'react';
import Image from 'next/image';
import styles from './ArtPeriodsOverviewSection.module.scss';
import { useAOS } from '@/src/lib/hooks/useAOS';
import Autoplay from 'embla-carousel-autoplay';
import { useRef } from 'react';

// ✅ Mantine Carousel
import { Carousel } from '@mantine/carousel';
import '@mantine/carousel/styles.css';
import Link from 'next/link';

type ArtPiece = {
    title: string;
    image: string;
    alt?: string;
    href: string;
};

type ArtPeriod = {
    key: string;
    title: string;
    description: string;
    pieces?: ArtPiece[];
    ctaLabel?: string;
    ctaHref?: string;
};

interface ArtPeriodsOverviewSectionProps {
    title?: string;
    subtitle?: string;
    periods: ArtPeriod[];
}


const ArtPeriodsOverviewSection: React.FC<ArtPeriodsOverviewSectionProps> = ({
    title = 'Œuvres',
    subtitle = 'Trois périodes pour raconter une vie transfigurée par l’art.',
    periods,
}) => {
    useAOS();

    const autoplay = useRef(
        Autoplay({
            delay: 3000,          // ⬅ time in ms
            stopOnInteraction: true,
        })
    );

    return (


        <section className={styles.section}>
            <header className={styles.header} data-aos="fade-up">
                <h2 className={styles.title}>{title}</h2>
                {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
            </header>


            <div className={styles.periods}>
                {periods.map((period) => (
                    <article key={period.key} className={styles.period} data-aos="fade-up">
                        <div className={styles.periodLeft}>
                            <h3 className={styles.periodTitle}>{period.title}</h3>
                            <p className={styles.periodDescription}>{period.description}</p>

                            {period.ctaLabel && period.ctaHref ? (
                                <Link className={styles.cta} href={period.ctaHref}>
                                    {period.ctaLabel}
                                </Link>
                            ) : null}
                        </div>

                        <div className={styles.periodRight}>
                            {period.pieces?.length ? (


                                <Carousel
                                    className={styles.carousel}
                                    withIndicators
                                    withControls={false}
                                    loop
                                    align="center"
                                    slideGap={0}
                                    slideSize="100%"
                                    dragFree
                                    plugins={[autoplay.current]}
                                >
                                    {period.pieces?.map((p, i) => (
                                        <Carousel.Slide key={`${period.key}-${i}`}>
                                            <div className={styles.slideCard}>
                                                <div className={styles.slideThumb}>
                                                    <Image
                                                        src={p.image}
                                                        alt={p.alt || p.title}
                                                        fill
                                                        sizes="(max-width: 1100px) 100vw, 1100px"
                                                        className={styles.slideImg}
                                                        priority={i === 0}
                                                    />
                                                </div>
                                            </div>
                                        </Carousel.Slide>
                                    ))}
                                </Carousel>
                            ) : (
                                <div className={styles.empty}>Aucune œuvre à afficher pour le moment.</div>
                            )}
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
};

export default ArtPeriodsOverviewSection;
